/**
 * The Newz Bridge bulkhead.
 *
 * A fixed layer *above* the application, never around it. The dashboard is
 * mounted, laid out and loading its data underneath this the whole time; these
 * panels only stop you looking at it. That is the entire trick, and it is why
 * `pointer-events` and `aria-hidden` are managed carefully — a decorative layer
 * must not steal focus or hide the application from a screen reader once it has
 * travelled out of the way.
 *
 * Everything that moves is a `transform`. Nothing here reads layout, and
 * nothing animates a property that would force one.
 *
 * The status printed on the architecture is *read*, never asserted. The
 * reference artwork shows "SYSTEM ONLINE" and "ALL SYSTEMS NOMINAL" on the
 * bulkhead; printing those as fixed text would be a fabricated production
 * value, so they come from the health endpoint and the seal state comes from
 * the state machine.
 */
import type { ReactNode } from 'react';
import { DOOR_EASING, ENTRANCE_TIMELINE, loginVisible, type EntranceState } from './machine';
import { useSystemStatus } from './useSystemStatus';

/** How the door describes itself. Derived from the machine, not decoration. */
function sealLabel(state: EntranceState): string {
  switch (state) {
    case 'LOCKED':
      return 'SEALED';
    case 'AUTHENTICATING':
      return 'VERIFYING';
    case 'ACCESS_GRANTED':
      return 'ACCESS GRANTED';
    case 'UNLOCKING':
      return 'UNLOCKING';
    default:
      return 'OPENING';
  }
}

/**
 * The Bridge emblem: an ascending chevron inside a ring.
 *
 * Deliberately generic industrial iconography — a direction and a boundary. No
 * insignia, badge or mark belonging to anybody else is reproduced.
 */
function Emblem() {
  return (
    <svg className="bridge__emblem" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <circle cx="32" cy="32" r="29" className="bridge__emblem-ring" />
      <path d="M32 13 L48 47 L32 38 L16 47 Z" className="bridge__emblem-mark" />
      <path d="M32 24 L40 41 L32 36.5 L24 41 Z" className="bridge__emblem-core" />
    </svg>
  );
}

function Panel({ side }: { side: 'left' | 'right' }) {
  return (
    <div className={`bridge__panel bridge__panel--${side}`} aria-hidden="true">
      <div className="bridge__plating" />
      <div className="bridge__inset bridge__inset--upper" />
      <div className="bridge__inset bridge__inset--lower" />
      <div className="bridge__rivets" />
      <div className="bridge__seam" />
      <div className="bridge__marking">
        <span className="bridge__marking-name">NEWZ BRIDGE</span>
        <span className="bridge__marking-note">AUTHORIZED ACCESS ONLY</span>
      </div>
      <div className="bridge__port" />
    </div>
  );
}

export function BridgeDoors({
  state,
  muted,
  onToggleMuted,
  children,
}: {
  state: EntranceState;
  muted: boolean;
  onToggleMuted(): void;
  children: ReactNode;
}) {
  const showLogin = loginVisible(state);
  const system = useSystemStatus();

  return (
    <div
      className="bridge"
      data-entrance={state}
      data-system={system.status}
      // Once the panels are travelling, the layer must stop intercepting
      // clicks: the dashboard behind it is live and should be usable the
      // moment it is visible, not once the animation politely finishes.
      style={
        {
          '--door-travel': `${ENTRANCE_TIMELINE.travelMs}ms`,
          '--door-easing': DOOR_EASING,
          '--login-fade': `${ENTRANCE_TIMELINE.loginFadeMs}ms`,
          '--reduced-fade': `${ENTRANCE_TIMELINE.reducedMotionMs}ms`,
        } as React.CSSProperties
      }
    >
      {/* The architecture the doors sit in. On a phone most of this is framing
          that would cost usable width for nothing, and is dropped. */}
      <div className="bridge__frame" aria-hidden="true">
        <div className="bridge__ceiling" />
        <div className="bridge__floor" />
        <div className="bridge__pillar bridge__pillar--left">
          <span className="bridge__strip" />
          <span className="bridge__strip bridge__strip--inner" />
          <span className="bridge__vent" />
        </div>
        <div className="bridge__pillar bridge__pillar--right">
          <span className="bridge__strip" />
          <span className="bridge__strip bridge__strip--inner" />
          <span className="bridge__vent" />
        </div>
      </div>

      <Panel side="left" />
      <Panel side="right" />

      {/* The centre join, lit from within. It brightens on unlock, which is
          the only visual cue that anything has happened before movement. */}
      <div className="bridge__join" aria-hidden="true" />

      {showLogin && (
        <>
          {/* Read from the health endpoint and from window.location. Nothing
              on this strip is a claim DCC cannot support. */}
          <div className="bridge__hud bridge__hud--top">
            <span className="bridge__hud-item" data-system={system.status}>
              <span className="bridge__hud-dot" aria-hidden="true" />
              {system.label}
            </span>
            <span className="bridge__hud-item bridge__hud-item--host">
              <span aria-hidden="true">◍</span>
              {system.host}
            </span>
          </div>

          <div className="bridge__hud bridge__hud--bottom">
            <span className="bridge__hud-item" data-system={system.status}>
              <span className="bridge__hud-dot" aria-hidden="true" />
              {system.detail}
            </span>
            <button
              type="button"
              className="bridge__mute"
              onClick={onToggleMuted}
              aria-pressed={muted}
            >
              <span aria-hidden="true">{muted ? '⊘' : '♪'}</span>
              {muted ? 'Sound off' : 'Sound on'}
            </button>
          </div>

          <span className="bridge__seal" aria-hidden="true">
            BRIDGE · {sealLabel(state)}
          </span>

          <div className="bridge__console">
            <div className="bridge__identity">
              <Emblem />
              <span className="bridge__wordmark">NEWZ BRIDGE</span>
              <span className="bridge__subtitle">AI Development Command Center</span>
            </div>
            {children}
          </div>
        </>
      )}
    </div>
  );
}
